import React, { useId } from 'react'

import type {
    MascotEarStyle,
    MascotFurType,
    MascotPattern,
    MascotSnout,
} from '../mascot-types'

/**
 * `portrait` is the plain floating head. `peek` crops the face at a ledge and
 * hooks paws over it, so the mascot reads as looking over a fence — the UI
 * edge it's positioned against supplies the fence itself.
 */
export type MascotPose = 'portrait' | 'peek'

export type MascotSvgProps = {
    size?: number | string
    primaryColor: string
    secondaryColor: string
    /** Iris colour. The pupil stays near-black. */
    eyeColor: string
    pattern: MascotPattern
    furType: MascotFurType
    /** Already resolved against the species — see `resolveMascotSnout`. */
    snout: MascotSnout
    /** Already resolved against the species — see `resolveMascotEarStyle`. */
    earStyle: MascotEarStyle
    pose?: MascotPose
    /** Accessible title for the SVG. */
    title?: string
}

export const MASCOT_VIEWBOX = '0 0 128 128'

/**
 * Peek framing is shared by every species: fence line at y=100 in a 128-tall
 * box. The box is wider than it is tall so hanging ears aren't cropped at
 * the left/right edges of a square — portrait crops are tight, peek has to
 * leave room for a drop to hang beside the skull.
 */
const MASCOT_PEEK_PAD_X = 28
export const MASCOT_PEEK_HEIGHT = 128
export const MASCOT_PEEK_WIDTH = MASCOT_PEEK_HEIGHT + 2 * MASCOT_PEEK_PAD_X
export const MASCOT_PEEK_VIEWBOX = `${-MASCOT_PEEK_PAD_X} 0 ${MASCOT_PEEK_WIDTH} ${MASCOT_PEEK_HEIGHT}`
export const MASCOT_PEEK_LEDGE_Y = 100
/** Fraction of the rendered height that sits above the fence line. */
export const MASCOT_PEEK_LEDGE_RATIO = MASCOT_PEEK_LEDGE_Y / MASCOT_PEEK_HEIGHT

/** Lowest point of the paws. There is deliberate empty box below this. */
const MASCOT_PEEK_PAW_BOTTOM_Y = MASCOT_PEEK_LEDGE_Y + 14
/**
 * Fraction of the rendered height down to the bottom of the paws. Position
 * against this rather than the SVG's bottom edge — the box extends past the
 * paws, so aligning by the edge silently eats most of the intended overlap.
 */
export const MASCOT_PEEK_PAW_BOTTOM_RATIO =
    MASCOT_PEEK_PAW_BOTTOM_Y / MASCOT_PEEK_HEIGHT

/**
 * Class on the peek pose's head group. Callers can rotate it (e.g. a curious
 * tilt on hover) without moving the paws, which stay gripped on the fence
 * because they're drawn outside this group.
 *
 * The group carries no `transform` attribute of its own — a CSS transform
 * would override the attribute rather than compose with it, which would wipe
 * out the species' peek placement.
 */
export const MASCOT_TILT_CLASS = 'chckvet-mascot-tilt'

export type MascotSvgIds = {
    headClip: string
    ledgeClip: string
}

/**
 * Ellipse the head roughly occupies. Shading and coat patterns are laid out
 * relative to this so each species gets markings that sit on its own face
 * instead of a shared set of hard-coded coordinates.
 */
export type MascotRegion = {
    cx: number
    cy: number
    rx: number
    ry: number
    /**
     * Y of the eye line. Masks and eye patches have to line up with the eyes,
     * which sit at a different height on a long horse face than a round cat
     * one, so the species supplies it rather than it being guessed from `cy`.
     */
    eyeY?: number
}

function eyeLine(region: MascotRegion): number {
    return region.eyeY ?? region.cy - region.ry * 0.12
}

/**
 * `size` is the face height. Portrait crops are square, so width matches.
 * Peek is wider than it is tall (room for hanging ears) — stretching that
 * crop into a square would shrink the head, so width follows the aspect.
 */
function svgSizeFromViewBox(
    size: number | string,
    viewBox: string,
): { width: number | string; height: number | string } {
    if (typeof size !== 'number') {
        return { width: size, height: size }
    }
    const parts = viewBox.trim().split(/[\s,]+/).map(Number)
    const vbW = parts[2]
    const vbH = parts[3]
    if (!vbW || !vbH || vbW === vbH) {
        return { width: size, height: size }
    }
    return {
        width: Math.round(size * (vbW / vbH)),
        height: size,
    }
}

/**
 * Shared SVG shell: publishes the coat CSS variables and mints a unique id
 * prefix so several avatars on one page don't collide on clipPath ids.
 */
export function MascotSvgRoot({
    size = 40,
    primaryColor,
    secondaryColor,
    eyeColor,
    title,
    viewBox = MASCOT_VIEWBOX,
    children,
}: MascotSvgProps & {
    /**
     * Species-specific tight crop. Each species frames its own artwork so the
     * face fills the avatar instead of floating in shared padding — this is
     * what makes a 22px trigger icon still legible.
     */
    viewBox?: string
    children: (ids: MascotSvgIds) => React.ReactNode
}) {
    const reactId = useId().replace(/:/g, '')
    const ids: MascotSvgIds = {
        headClip: `mascot-head-${reactId}`,
        ledgeClip: `mascot-ledge-${reactId}`,
    }
    const { width, height } = svgSizeFromViewBox(size, viewBox)

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={viewBox}
            width={width}
            height={height}
            overflow="visible"
            role={title ? 'img' : 'presentation'}
            aria-hidden={title ? undefined : true}
            aria-label={title}
            style={
                {
                    ['--coat-primary' as string]: primaryColor,
                    ['--coat-secondary' as string]: secondaryColor,
                    ['--coat-iris' as string]: eyeColor,
                    ['--coat-line' as string]: 'rgba(32, 26, 22, 0.42)',
                    ['--coat-eye' as string]: '#231d18',
                    ['--coat-flesh' as string]: '#e79aa8',
                    display: 'block',
                    flexShrink: 0,
                } as React.CSSProperties
            }
        >
            {title ? <title>{title}</title> : null}
            {children(ids)}
        </svg>
    )
}

const COAT_STROKE = 2.4

/** Standard coat fill + outline, so every species reads the same weight. */
export const coatFill = {
    fill: 'var(--coat-primary)',
    stroke: 'var(--coat-line)',
    strokeWidth: COAT_STROKE,
    strokeLinejoin: 'round' as const,
}

/**
 * Hairline for the flat artwork, in 128-space units. The illustrated set has
 * no outlines, which looks great until a clinic picks a white or cream coat
 * and the mascot vanishes against the white dock. This is thin enough to read
 * as "no outline" on mid and dark coats — the line colour is dark and
 * translucent, so it sinks into anything but a pale fill — while still
 * holding the silhouette on white.
 *
 * Head paths are drawn inside a `scale(0.25)` group, so they need
 * {@link FLAT_ART_STROKE_WIDTH} rather than this value.
 */
export const FLAT_COAT_STROKE_WIDTH = 1.2
/** Same visual weight, pre-multiplied for art drawn on the 512 grid. */
export const FLAT_ART_STROKE_WIDTH = FLAT_COAT_STROKE_WIDTH * 4

/**
 * Coat fill for the flat (outline-free) artwork. Same colour source as
 * {@link coatFill}, so flat and outlined species can share shape libraries
 * without looking like two different sets.
 */
export const flatCoatFill = {
    fill: 'var(--coat-primary)',
    stroke: 'var(--coat-line)',
    strokeWidth: FLAT_COAT_STROKE_WIDTH,
    strokeLinejoin: 'round' as const,
}

/** Head-shape outline for flat art drawn on the original 512 grid. */
export const flatArtOutline = {
    stroke: 'var(--coat-line)',
    strokeWidth: FLAT_ART_STROKE_WIDTH,
    strokeLinejoin: 'round' as const,
}

export const accentFill = {
    fill: 'var(--coat-secondary)',
    stroke: 'var(--coat-line)',
    strokeWidth: 1.6,
    strokeLinejoin: 'round' as const,
}

/**
 * Top highlight + underside shade, clipped to the head. Gives the flat coat
 * a bit of volume without needing per-coat colour math.
 */
export function MascotHeadShading({
    clipPathId,
    region,
}: {
    clipPathId: string
    region: MascotRegion
}) {
    const { cx, cy, rx, ry } = region
    return (
        <g clipPath={`url(#${clipPathId})`}>
            <ellipse
                cx={cx}
                cy={cy - ry * 0.62}
                rx={rx * 0.74}
                ry={ry * 0.42}
                fill="#fff"
                opacity={0.16}
            />
            <ellipse
                cx={cx}
                cy={cy + ry * 1.06}
                rx={rx * 1.05}
                ry={ry * 0.5}
                fill="#000"
                opacity={0.09}
            />
        </g>
    )
}

/**
 * Fixed scatter for `speckled`, in normalised head coordinates (-1..1). Kept
 * as a constant rather than randomised so a mascot looks the same on every
 * render and across every avatar on the page.
 */
const TICK_OFFSETS: [number, number][] = [
    // Outer ring — ticking is heaviest around the edge of the face.
    [0.86, 0.02],
    [0.78, 0.33],
    [0.6, 0.61],
    [0.31, 0.79],
    [-0.02, 0.85],
    [-0.33, 0.77],
    [-0.61, 0.59],
    [-0.79, 0.31],
    [-0.85, -0.03],
    [-0.77, -0.34],
    [-0.59, -0.6],
    [-0.32, -0.78],
    [0.03, -0.84],
    [0.34, -0.76],
    [0.62, -0.58],
    [0.79, -0.3],
    // Middle ring, rotated off the outer one so they don't line up in spokes.
    [0.56, 0.16],
    [0.42, 0.4],
    [0.14, 0.57],
    [-0.16, 0.55],
    [-0.41, 0.42],
    [-0.57, 0.14],
    [-0.55, -0.16],
    [-0.4, -0.42],
    [-0.14, -0.56],
    [0.16, -0.55],
    [0.41, -0.4],
    [0.57, -0.13],
    // Light scatter over the middle of the face.
    [0.3, 0.13],
    [0.11, 0.3],
    [-0.13, 0.29],
    [-0.29, 0.11],
    [-0.3, -0.13],
    [-0.11, -0.29],
    [0.13, -0.3],
    [0.29, -0.11],
]

/**
 * Coat markings clipped to the head.
 *
 * All are drawn relative to the species' head region so they land on the
 * face rather than at fixed coordinates:
 * - `stripes` — tabby/brindle: the forehead "M" plus stripes sweeping back
 *   off the cheeks. The M is what actually makes it read as a tabby.
 * - `spots` — a few large, asymmetric spots.
 * - `patched` — piebald / pinto: two big irregular patches.
 * - `speckled` — roan / ticked: fine flecks over the whole head.
 * - `bicolor` — light lower face under a coloured cap.
 * - `blaze` — stripe down the centre of the face.
 * - `mask` — band across the eyes.
 * - `eye_patch` — one eye ringed, the other left plain.
 *
 * Placed markings live in this same list rather than a second picker: the
 * mascot is head-only, so a "face marking" and a "coat pattern" would paint
 * the same real estate in the same colour, and most pairings just read as
 * mud.
 */
export function MascotPatternLayer({
    pattern,
    clipPathId,
    region,
}: {
    pattern: MascotPattern
    clipPathId: string
    region: MascotRegion
}) {
    if (pattern === 'solid') return null

    const { cx, cy, rx, ry } = region
    const clip = `url(#${clipPathId})`

    if (pattern === 'speckled') {
        // Roan / ticked: fine flecks, denser toward the outside of the face.
        // Evenly spaced dots of a single size read as a rash rather than as
        // hair, so both the size and the spacing vary.
        return (
            <g clipPath={clip} opacity={0.3}>
                {TICK_OFFSETS.map(([ox, oy], i) => (
                    <circle
                        key={`${ox}-${oy}`}
                        cx={cx + rx * ox}
                        cy={cy + ry * oy}
                        r={rx * (0.018 + (i % 4) * 0.005)}
                        fill="var(--coat-secondary)"
                    />
                ))}
            </g>
        )
    }

    const eyeY = region.eyeY ?? cy - ry * 0.12

    if (pattern === 'bicolor') {
        // Irish spotting — the beagle / tuxedo / bald-face pattern: a white
        // muzzle running up into a broad blaze between the eyes and over the
        // crown, with colour left on the sides of the face and the ears.
        //
        // The blaze has to be wide enough, and carry straight up, for the
        // muzzle and the forehead to read as ONE connected white area. A
        // narrow wedge that pinches at the brow reads as two separate white
        // patches that happen to be near each other, which is what this
        // looked like before.
        //
        // Drawn as the LIGHT area rather than as two coloured patches on
        // purpose — the ears are painted from the primary coat and aren't part
        // of this layer, so colouring the sides would leave mismatched ears.
        // Inverting it keeps the sides and the ears the same colour.
        return (
            <g clipPath={clip} opacity={0.92}>
                <path
                    d={[
                        `M${cx - rx * 1.5} ${cy + ry * 1.55}`,
                        `C ${cx - rx * 1.1} ${cy + ry * 0.1} ${cx - rx * 0.7} ${eyeY + ry * 0.1} ${cx - rx * 0.34} ${eyeY - ry * 0.15}`,
                        `C ${cx - rx * 0.3} ${cy - ry * 0.7} ${cx - rx * 0.28} ${cy - ry * 1.1} ${cx - rx * -1} ${cy - ry * 1.6}`,
                        `L${cx + rx * -1} ${cy - ry * 1.6}`,
                        `C ${cx + rx * 0.28} ${cy - ry * 1.1} ${cx + rx * 0.3} ${cy - ry * 0.7} ${cx + rx * 0.34} ${eyeY - ry * 0.15}`,
                        `C ${cx + rx * 0.7} ${eyeY + ry * 0.1} ${cx + rx * 1.1} ${cy + ry * 0.1} ${cx + rx * 1.5} ${cy + ry * 1.55}`,
                        `L${cx + rx * 1.5} ${cy + ry * 2}`,
                        `L${cx - rx * 1.5} ${cy + ry * 2}`,
                        'Z',
                    ].join(' ')}
                    fill="var(--coat-secondary)"
                />
            </g>
        )
    }

    if (pattern === 'blaze') {
        return (
            <g clipPath={clip} opacity={0.9}>
                <path
                    d={`M${cx - rx * 0.14} ${cy - ry * 1.25} C ${cx - rx * 0.22} ${cy - ry * 0.45} ${cx - rx * 0.17} ${cy + ry * 0.2} ${cx - rx * 0.13} ${cy + ry * 1.3} L${cx + rx * 0.13} ${cy + ry * 1.3} C ${cx + rx * 0.17} ${cy + ry * 0.2} ${cx + rx * 0.22} ${cy - ry * 0.45} ${cx + rx * 0.14} ${cy - ry * 1.25} Z`}
                    fill="var(--coat-secondary)"
                />
            </g>
        )
    }

    if (pattern === 'mask') {
        // A mask is the DARK muzzle of a shepherd or a pug, or the points of
        // a siamese — it covers the muzzle and reaches up around the eyes,
        // dipping between them. Drawn before as a pale band straight across
        // the eyes, which is a sleep mask, not a coat marking.
        //
        // Painted as shade rather than from the secondary coat, because a
        // mask is always darker than the animal it's on. Letting it take an
        // arbitrary colour is exactly what produced the pale band.
        return (
            <g clipPath={clip} opacity={0.34}>
                <path
                    d={[
                        `M${cx - rx * 0.92} ${eyeY + ry * 0.12}`,
                        `C ${cx - rx * 0.98} ${eyeY - ry * 0.42} ${cx - rx * 0.46} ${eyeY - ry * 0.52} ${cx - rx * 0.32} ${eyeY - ry * 0.06}`,
                        `C ${cx - rx * 0.16} ${cy + ry * 0.16} ${cx + rx * 0.16} ${cy + ry * 0.16} ${cx + rx * 0.32} ${eyeY - ry * 0.06}`,
                        `C ${cx + rx * 0.46} ${eyeY - ry * 0.52} ${cx + rx * 0.98} ${eyeY - ry * 0.42} ${cx + rx * 0.92} ${eyeY + ry * 0.12}`,
                        `C ${cx + rx * 1.05} ${cy + ry * 1.5} ${cx - rx * 1.05} ${cy + ry * 1.5} ${cx - rx * 0.92} ${eyeY + ry * 0.12}`,
                        'Z',
                    ].join(' ')}
                    fill="#000"
                />
            </g>
        )
    }

    if (pattern === 'eye_patch') {
        return (
            <g clipPath={clip} opacity={0.85}>
                <ellipse
                    cx={cx - rx * 0.44}
                    cy={eyeY + ry * 0.02}
                    rx={rx * 0.4}
                    ry={ry * 0.36}
                    fill="var(--coat-secondary)"
                    transform={`rotate(-12 ${cx - rx * 0.44} ${eyeY})`}
                />
            </g>
        )
    }

    if (pattern === 'patched') {
        return (
            <g clipPath={clip} opacity={0.8} fill="var(--coat-secondary)">
                <path
                    d={`M${cx - rx * 1.3} ${cy - ry * 1.3} C ${cx - rx * 0.2} ${cy - ry * 1.4} ${cx + rx * 0.36} ${cy - ry * 0.76} ${cx + rx * 0.02} ${cy - ry * 0.12} C ${cx - rx * 0.22} ${cy + ry * 0.32} ${cx - rx * 0.82} ${cy + ry * 0.44} ${cx - rx * 1.3} ${cy + ry * 0.26} Z`}
                />
                <path
                    d={`M${cx + rx * 0.34} ${cy + ry * 0.52} C ${cx + rx * 0.76} ${cy + ry * 0.3} ${cx + rx * 1.2} ${cy + ry * 0.56} ${cx + rx * 1.3} ${cy + ry * 1.15} C ${cx + rx * 0.8} ${cy + ry * 1.35} ${cx + rx * 0.34} ${cy + ry * 1.05} ${cx + rx * 0.34} ${cy + ry * 0.52} Z`}
                />
            </g>
        )
    }

    // Tabby. Forehead strokes fan outward as they descend, forming the "M".
    const mTop = cy - ry * 0.95
    const mBottom = cy - ry * 0.52
    const mOffsets = [-0.24, -0.12, 0, 0.12, 0.24]
    const cheekStripes = [
        `M${cx - rx * 1.06} ${cy - ry * 0.32} Q ${cx - rx * 0.74} ${cy - ry * 0.06} ${cx - rx * 0.42} ${cy - ry * 0.02}`,
        `M${cx - rx * 1.02} ${cy + ry * 0.12} Q ${cx - rx * 0.7} ${cy + ry * 0.32} ${cx - rx * 0.4} ${cy + ry * 0.32}`,
        `M${cx + rx * 1.06} ${cy - ry * 0.32} Q ${cx + rx * 0.74} ${cy - ry * 0.06} ${cx + rx * 0.42} ${cy - ry * 0.02}`,
        `M${cx + rx * 1.02} ${cy + ry * 0.12} Q ${cx + rx * 0.7} ${cy + ry * 0.32} ${cx + rx * 0.4} ${cy + ry * 0.32}`,
    ]

    return (
        <g
            clipPath={clip}
            opacity={0.55}
            fill="none"
            stroke="var(--coat-secondary)"
            strokeLinecap="round"
        >
            {mOffsets.map(o => (
                <path
                    key={o}
                    d={`M${cx + rx * o * 0.55} ${mTop} L${cx + rx * o * 1.5} ${mBottom}`}
                    strokeWidth={ry * 0.1}
                />
            ))}
            {cheekStripes.map(d => (
                <path key={d} d={d} strokeWidth={ry * 0.11} />
            ))}
        </g>
    )
}

/**
 * Wraps a species' portrait artwork for the requested pose.
 *
 * In `peek` the art is scaled/shifted into the shared peek box by
 * `peekTransform` and clipped at the fence line, then the paws are drawn
 * unclipped so they hang over the edge onto whatever UI sits below.
 */
export function MascotPoseFrame({
    pose = 'portrait',
    ids,
    peekTransform,
    paws,
    children,
}: {
    pose?: MascotPose
    ids: MascotSvgIds
    peekTransform: string
    paws: React.ReactNode
    children: React.ReactNode
}) {
    if (pose !== 'peek') return <>{children}</>

    return (
        <>
            <defs>
                <clipPath id={ids.ledgeClip}>
                    <rect
                        x="-64"
                        y="-64"
                        width="256"
                        height={MASCOT_PEEK_LEDGE_Y + 64}
                    />
                </clipPath>
            </defs>
            <g clipPath={`url(#${ids.ledgeClip})`}>
                {/* Tilt pivots on the fence line, so the head leans out of a
                    clip that stays put. */}
                <g
                    className={MASCOT_TILT_CLASS}
                    style={{
                        transformBox: 'view-box',
                        transformOrigin: `50% ${MASCOT_PEEK_LEDGE_Y}px`,
                    }}
                >
                    <g transform={peekTransform}>{children}</g>
                </g>
            </g>
            {paws}
        </>
    )
}

/**
 * A single limb hooked over the fence.
 *
 * The toes scallop the BOTTOM edge: what you see when an animal drapes a paw
 * over a rail is the back of the paw above it and the toes curling down over
 * the front. `hoof` swaps them for a banded block so the horse doesn't end up
 * with fingers.
 */
export function MascotLimb({
    x0,
    x1,
    kind = 'paw',
}: {
    x0: number
    x1: number
    kind?: 'paw' | 'hoof'
}) {
    const top = MASCOT_PEEK_LEDGE_Y - 9
    const bottom = MASCOT_PEEK_LEDGE_Y + 14
    const topRadius = 4
    const w = x1 - x0

    if (kind === 'hoof') {
        const r = 7
        return (
            <g>
                <path
                    d={`M${x0 + topRadius} ${top} H${x1 - topRadius} A${topRadius} ${topRadius} 0 0 1 ${x1} ${top + topRadius} V${bottom - r} A${r} ${r} 0 0 1 ${x1 - r} ${bottom} H${x0 + r} A${r} ${r} 0 0 1 ${x0} ${bottom - r} V${top + topRadius} A${topRadius} ${topRadius} 0 0 1 ${x0 + topRadius} ${top} Z`}
                    {...coatFill}
                />
                <path
                    d={`M${x0} ${bottom - 10} H${x1} V${bottom - r} A${r} ${r} 0 0 1 ${x1 - r} ${bottom} H${x0 + r} A${r} ${r} 0 0 1 ${x0} ${bottom - r} Z`}
                    fill="var(--coat-secondary)"
                    opacity={0.85}
                />
            </g>
        )
    }

    // Three toes across the bottom: each is a half-circle of diameter w/3, so
    // the scallops exactly fill the paw's width.
    const toe = w / 6
    const toeLine = bottom - toe
    const d = [
        `M${x0 + topRadius} ${top}`,
        `H${x1 - topRadius}`,
        `A${topRadius} ${topRadius} 0 0 1 ${x1} ${top + topRadius}`,
        `V${toeLine}`,
        `A${toe} ${toe} 0 0 1 ${x1 - 2 * toe} ${toeLine}`,
        `A${toe} ${toe} 0 0 1 ${x1 - 4 * toe} ${toeLine}`,
        `A${toe} ${toe} 0 0 1 ${x0} ${toeLine}`,
        `V${top + topRadius}`,
        `A${topRadius} ${topRadius} 0 0 1 ${x0 + topRadius} ${top}`,
        'Z',
    ].join(' ')

    return <path d={d} {...coatFill} />
}

/** Both limbs, positioned to flank the face. */
export function MascotLimbPair({ kind = 'paw' }: { kind?: 'paw' | 'hoof' }) {
    const inset = kind === 'hoof' ? 6 : 0
    return (
        <>
            <MascotLimb x0={20 + inset} x1={48 + inset} kind={kind} />
            <MascotLimb x0={80 - inset} x1={108 - inset} kind={kind} />
        </>
    )
}

export function MascotEye({
    cx,
    cy,
    r = 4.4,
}: {
    cx: number
    cy: number
    r?: number
}) {
    return (
        <g>
            {/* Faint socket so the eye still separates on near-black coats */}
            <circle cx={cx} cy={cy} r={r * 1.32} fill="#fff" opacity={0.2} />
            <circle cx={cx} cy={cy} r={r} fill="var(--coat-iris)" />
            <circle cx={cx} cy={cy} r={r * 0.58} fill="var(--coat-eye)" />
            <circle
                cx={cx - r * 0.3}
                cy={cy - r * 0.34}
                r={r * 0.3}
                fill="#fff"
                opacity={0.92}
            />
            <circle
                cx={cx + r * 0.32}
                cy={cy + r * 0.3}
                r={r * 0.14}
                fill="#fff"
                opacity={0.5}
            />
        </g>
    )
}

/**
 * Eye for the flat artwork: an upright almond rather than the assets' capsule,
 * which reads more like an eye and less like a pill.
 *
 * Drawn as iris + pupil so the clinic's eye colour is visible without the eye
 * turning into a costume lens — at avatar sizes the pupil is most of what you
 * see, and the iris reads as a coloured rim. `rx`/`ry` are half-extents in the
 * species' own portrait space.
 */
export function MascotFlatEye({
    cx,
    cy,
    rx,
    ry,
}: {
    cx: number
    cy: number
    rx: number
    ry: number
}) {
    // Almond: pointed at the inner and outer corners, curved above and below.
    const almond = [
        `M${cx - rx} ${cy}`,
        `C${cx - rx} ${cy - ry * 0.72} ${cx - rx * 0.5} ${cy - ry} ${cx} ${cy - ry}`,
        `C${cx + rx * 0.5} ${cy - ry} ${cx + rx} ${cy - ry * 0.72} ${cx + rx} ${cy}`,
        `C${cx + rx} ${cy + ry * 0.72} ${cx + rx * 0.5} ${cy + ry} ${cx} ${cy + ry}`,
        `C${cx - rx * 0.5} ${cy + ry} ${cx - rx} ${cy + ry * 0.72} ${cx - rx} ${cy}`,
        'Z',
    ].join(' ')

    const pupilRx = rx * 0.62
    const pupilRy = ry * 0.66

    return (
        <g>
            <path d={almond} fill="var(--coat-iris)" />
            {/* Rim, so a pale iris still separates from a pale coat. */}
            <path
                d={almond}
                fill="none"
                stroke="var(--coat-eye)"
                strokeWidth={rx * 0.14}
                opacity={0.55}
            />
            <ellipse
                cx={cx}
                cy={cy}
                rx={pupilRx}
                ry={pupilRy}
                fill="var(--coat-eye)"
            />
            <circle
                cx={cx - rx * 0.3}
                cy={cy - ry * 0.36}
                r={rx * 0.3}
                fill="#fff"
                opacity={0.95}
            />
            <circle
                cx={cx + rx * 0.3}
                cy={cy + ry * 0.34}
                r={rx * 0.14}
                fill="#fff"
                opacity={0.55}
            />
        </g>
    )
}
